
import React, { useRef, useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { BottomNav } from '../components/BottomNav';
import { MiniAudioPlayer } from '../components/MiniAudioPlayer';
import { FloatingSupport } from '../components/FloatingSupport';
import { OfflineNotice } from '../components/OfflineNotice';
import { Breadcrumbs } from '../components/Breadcrumbs';

// --- ROUTE CONFIGURATION ---
// Determines layout behavior per route
const ROUTE_CONFIG: Record<string, { mode: 'fixed' | 'scroll', tab: string }> = {
  '/home': { mode: 'scroll', tab: 'home' },
  '/catalog': { mode: 'fixed', tab: 'catalog' }, // Fixed for sticky filters
  '/favorites': { mode: 'fixed', tab: 'profile' }, // Uses Catalog layout
  '/wishlist': { mode: 'scroll', tab: 'profile' },
  '/club': { mode: 'fixed', tab: 'live' }, // Fixed for sticky tabs
  '/tracking': { mode: 'scroll', tab: 'orders' },
  '/profile': { mode: 'scroll', tab: 'profile' },
  '/addresses': { mode: 'scroll', tab: 'profile' },
};

export const MainLayout = () => {
    const location = useLocation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    // Resolve Configuration
    const path = location.pathname;
    let config = ROUTE_CONFIG[path];
    
    if (!config) {
        // Fallback for sub-routes
        const key = Object.keys(ROUTE_CONFIG).find(k => path.startsWith(k));
        config = key ? ROUTE_CONFIG[key] : { mode: 'scroll', tab: 'home' };
    }

    const isFixedLayout = config.mode === 'fixed';

    // SCROLL RESTORATION
    useLayoutEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
    }, [location.pathname]);

    return (
        <div className="h-screen-safe w-full flex justify-center bg-[#e7e5e4] dark:bg-[#0f0c0a] overflow-hidden">
            
            {/* Global Audio Player - Floats on top */}
            <MiniAudioPlayer />
            
            {/* Columna Central de la App */}
            <div className="w-full md:max-w-xl lg:max-w-2xl bg-background-light dark:bg-background-dark h-full shadow-2xl relative flex flex-col border-x border-black/5 dark:border-white/5">
                
                {/* Global Support Widget - Now inside the relative column */}
                <FloatingSupport />

                {/* Offline Notice within the app shell */}
                <OfflineNotice />

                {/* 
                    LAYOUT MODES:
                    - Scroll: The div itself scrolls. Good for simple pages.
                    - Fixed: The div clips. The child (Outlet) must handle scrolling internally. 
                      Good for pages with complex sticky headers (Catalog, Club).
                */}
                <div 
                    ref={scrollContainerRef}
                    className={`flex-1 w-full relative ${isFixedLayout ? 'overflow-hidden flex flex-col' : 'overflow-y-auto overflow-x-hidden scroll-smooth no-scrollbar'}`}
                >
                     {/* Breadcrumbs Navigation */}
                     <Breadcrumbs />
                     
                     {/* Key forces re-render of animation on route change */}
                     <div className={`animate-fade-in ${isFixedLayout ? 'h-full flex flex-col' : 'min-h-full'}`}>
                        <Outlet />
                     </div>
                </div>

                {/* Bottom Navigation - Floating Glassmorphism */}
                <div className="absolute bottom-0 w-full z-50 pointer-events-none">
                    <BottomNav active={config.tab} />
                </div>
            </div>
        </div>
    );
};
